import type { StageAllocation } from '../../types/strategy'
import type { AllocationSimulationResponse } from '../../types/simulation'
import { STAGE_RECIPES } from '../capital/stageStaticSteps'

function usd(n: number) {
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function sol(v: number, basePrice: number) {
  if (basePrice <= 0) return '—'
  return (v / basePrice).toFixed(4) + ' SOL/day'
}
function signedUsd(n: number) {
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return (n >= 0 ? '+$' : '-$') + abs
}

const STAGE_ICON: Record<string, string> = {
  STAKE: 'S', LEND: 'L', BORROW: 'B', LP: 'LP', HEDGE: 'H', LOOP: '∞',
}
const STAGE_TEXT: Record<string, string> = {
  STAKE: 'text-emerald-400', LEND: 'text-accent-blue', BORROW: 'text-amber-400',
  LP: 'text-accent-purple', HEDGE: 'text-red-400', LOOP: 'text-indigo-400',
}

function gapColor(gap: number, tol: number): string {
  if (Math.abs(gap) <= tol) return 'text-emerald-400'
  return gap < 0 ? 'text-red-400' : 'text-amber-400'
}

function gapBg(gap: number, tol: number): string {
  if (Math.abs(gap) <= tol) return 'border-emerald-500/20 bg-emerald-500/[0.04]'
  return gap < 0 ? 'border-red-500/20 bg-red-500/[0.04]' : 'border-amber-500/20 bg-amber-500/[0.04]'
}

interface Props {
  result: AllocationSimulationResponse
  stageKeys: string[]
  stageMap: Record<string, StageAllocation>
  realStageMap: Record<string, StageAllocation> | null
  onBack: () => void
  onRestart: () => void
  onExecute: () => void
  onGoToStage: (stageKey: string) => void
}

export function SummaryStep({ result, stageKeys, stageMap, realStageMap, onBack, onRestart, onExecute, onGoToStage }: Props) {
  const alloc = result.allocation
  const apyPct = (alloc.theoreticalNetApyPct * 100).toFixed(1)
  const riskColor =
    alloc.compositeRiskScore <= 3 ? 'text-emerald-400' :
    alloc.compositeRiskScore <= 6 ? 'text-amber-400' : 'text-red-400'

  return (
    <div className="space-y-5">
      {/* Hero */}
      <div className="rounded-xl border border-accent-purple/20 bg-accent-purple/5 p-6 text-center">
        <div className="w-14 h-14 rounded-full bg-accent-purple/20 border border-accent-purple/30 flex items-center justify-center mx-auto mb-3">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-7 h-7 text-accent-purple">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-white mb-1">Your plan is ready</h2>
        <p className="text-sm text-gray-400 mb-4">Follow the steps in order — each one builds on the previous.</p>

        {/* Big APY number */}
        <div className="inline-flex flex-col items-center bg-surface-card rounded-xl border border-white/5 px-8 py-4">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">Projected Net APY</span>
          <span className="text-4xl font-bold text-accent-purple">{apyPct}%</span>
          <span className="text-xs text-gray-400 mt-1">after all costs</span>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-3 text-center">
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">Daily yield</div>
          <div className="text-sm font-bold text-emerald-400 tabular-nums">
            {usd(alloc.netCarryPerDayUsd)}
          </div>
          <div className="text-[9px] text-gray-600 mt-0.5">
            {sol(alloc.netCarryPerDayUsd, alloc.marketContext.basePrice)}
          </div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-3 text-center">
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">30-day est.</div>
          <div className="text-sm font-bold text-emerald-400 tabular-nums">
            {usd(alloc.projectedGrowth30dUsd)}
          </div>
          <div className="text-[9px] text-gray-600 mt-0.5">projected growth</div>
        </div>
        <div className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-3 text-center">
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">Risk score</div>
          <div className={`text-sm font-bold tabular-nums ${riskColor}`}>
            {alloc.compositeRiskScore.toFixed(1)} / 10
          </div>
          <div className="text-[9px] text-gray-600 mt-0.5">composite</div>
        </div>
      </div>

      {/* Real Capital Allocation */}
      <div className="rounded-xl border border-white/5 bg-surface-card p-4">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">Real capital allocation</div>
        <div className="space-y-2">
          {stageKeys.map(key => {
            const plan  = stageMap[key]
            const real  = realStageMap?.[key]
            const recipe = STAGE_RECIPES[key]
            if (!plan) return null

            const target = plan.amountUsd
            const actual = real?.actualAmountUsd ?? null
            const gap    = real?.gapUsd ?? null
            const tol    = Math.max(1.0, 0.005 * target)

            return (
              <button
                key={key}
                type="button"
                onClick={() => onGoToStage(key)}
                className={`w-full rounded-lg border p-3 text-left transition-colors hover:brightness-110 ${
                  gap != null ? gapBg(gap, tol) : 'border-white/5 bg-white/[0.02]'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-7 h-7 rounded-full bg-white/5 flex items-center justify-center text-[10px] font-bold shrink-0 ${STAGE_TEXT[key] ?? 'text-gray-400'}`}>
                    {STAGE_ICON[key] ?? key.charAt(0)}
                  </span>
                  <span className={`text-xs font-semibold w-12 shrink-0 ${STAGE_TEXT[key] ?? 'text-gray-300'}`}>
                    {recipe?.title ?? key}
                  </span>
                  <div className="flex-1 flex items-center justify-end gap-3 text-[11px] tabular-nums">
                    <div className="text-right">
                      <div className="text-[9px] text-gray-600 uppercase tracking-wider">Target</div>
                      <div className="text-gray-300">{usd(target)}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] text-gray-600 uppercase tracking-wider">Actual</div>
                      <div className="text-gray-300">{actual != null ? usd(actual) : '—'}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[9px] text-gray-600 uppercase tracking-wider">Gap</div>
                      <div className={gap != null ? gapColor(gap, tol) : 'text-gray-500'}>
                        {gap != null ? signedUsd(gap) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            )
          })}
        </div>
        {!realStageMap && (
          <p className="mt-2 text-[10px] text-gray-600">Connect wallet to see actual vs. target</p>
        )}
      </div>

      {/* Narrative if available */}
      {result.narrative?.headline && (
        <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-300 mb-1.5">Strategy insight</div>
          <p className="text-sm text-indigo-100 leading-relaxed">{result.narrative.headline}</p>
        </div>
      )}

      {/* First steps guidance */}
      {result.narrative?.firstSteps?.length ? (
        <div className="rounded-xl border border-white/5 bg-surface-card p-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-3">First steps</div>
          <ol className="space-y-2">
            {result.narrative.firstSteps.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-[12px] text-gray-300">
                <span className="shrink-0 w-5 h-5 rounded-full bg-accent-purple/15 text-accent-purple text-[10px] font-semibold flex items-center justify-center">
                  {i + 1}
                </span>
                <span className="leading-relaxed">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {/* Actions */}
      <div className="flex gap-3">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-white/10 text-gray-300 text-sm hover:bg-white/5 transition-colors"
        >
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-3.5 h-3.5"><polyline points="10 3 5 8 10 13" /></svg>
          Back
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-white/10 text-gray-200 text-sm hover:bg-white/5 transition-colors"
        >
          Change strategy
        </button>
        <button
          type="button"
          onClick={onExecute}
          className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-accent-purple text-white font-semibold text-sm hover:opacity-90 transition-opacity"
        >
          Execute
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="w-4 h-4"><polyline points="6 3 11 8 6 13" /></svg>
        </button>
      </div>
    </div>
  )
}
